import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';
import { ApiClient } from '../../services/api';

// Constants
const MIN_DURATION_MINUTES = 5;
const BASE_QUERY_KEY = `lead/demos/<demoId>/recordings`;

// Utility function to select the best video recording from available recordings
const selectBestVideoRecording = (
	allRecordings: IDemoRecordingFile[],
	fallbackRecording?: IDemoRecordingFile
): IDemoRecordingFile | undefined => {
	if (!allRecordings?.length) return fallbackRecording;

	// First, try to find speaker_view (seems to work better)
	let actualVideoRecording = allRecordings.find(rec => rec.type === 'shared_screen_with_speaker_view' && rec.playUrl);

	// If no speaker_view, try active_speaker
	if (!actualVideoRecording) {
		actualVideoRecording = allRecordings.find(rec => rec.type === 'active_speaker' && rec.playUrl);
	}

	// If still nothing, try other video types (but deprioritize gallery_view)
	if (!actualVideoRecording) {
		actualVideoRecording = allRecordings.find(
			rec => rec.type?.toLowerCase().includes('video') && rec.type !== 'shared_screen_with_gallery_view' && rec.playUrl
		);
	}

	// As a last resort, try gallery_view or any MP4 with playUrl
	if (!actualVideoRecording) {
		actualVideoRecording = allRecordings.find(
			rec =>
				(rec.type === 'shared_screen_with_gallery_view' || rec.fileType?.toLowerCase() === 'mp4') &&
				rec.playUrl &&
				!rec.type?.toLowerCase().includes('summary') &&
				!rec.type?.toLowerCase().includes('transcript') &&
				!rec.type?.toLowerCase().includes('chat')
		);
	}

	// Final fallback to original recording
	return actualVideoRecording || fallbackRecording;
};

// Utility function to select the best transcript from available recordings
const selectBestTranscript = (
	allRecordings: IDemoRecordingFile[],
	fallbackTranscript?: IDemoRecordingFile
): IDemoRecordingFile | undefined => {
	if (!allRecordings?.length) return fallbackTranscript;

	return (
		allRecordings.find(
			rec =>
				rec.type?.toLowerCase().includes('transcript') ||
				rec.fileType?.toLowerCase() === 'vtt' ||
				rec.fileType?.toLowerCase() === 'txt'
		) || fallbackTranscript
	);
};

export interface IDemoRecordingParticipant {
	name: string;
	email: string;
	joinTime: string;
	leaveTime: string;
	duration: number;
}

export interface IDemoRecordingFile {
	type: string;
	fileType: string;
	fileSize: number;
	playUrl?: string;
	downloadUrl?: string;
	shareUrl?: string;
	recordingStart: string;
	recordingEnd: string;
	status: string;
}

export interface IDemoRecording {
	id: string;
	uuid: string;
	topic: string;
	startTime: string;
	duration: number;
	totalSize: number;
	recordingCount: number;
	videoRecording?: IDemoRecordingFile;
	transcript?: IDemoRecordingFile;
	participants: IDemoRecordingParticipant[];
	externalParticipants: IDemoRecordingParticipant[];
	allRecordings: IDemoRecordingFile[];
	// Add computed fields for better recording selection
	actualVideoRecording?: IDemoRecordingFile;
	actualTranscript?: IDemoRecordingFile;
}

export interface IDemoRecordingsResponse {
	values: IDemoRecording[];
	totalCount: number;
	pageToken?: string;
}

interface IProcessedDemoRecordings {
	recordings: IDemoRecording[];
	bestMatchIndex: number | null;
}

// Process recordings with filtering and best match logic
const processRecordings = (recordingData: IDemoRecording[], demoDate?: string): IProcessedDemoRecordings => {
	// Filter recordings to only include those from the same date as the demo
	// and exclude recordings with zero duration or less than minimum duration
	let filteredRecordings = recordingData;

	if (demoDate) {
		const demoDateOnly = new Date(demoDate).toISOString().split('T')[0];

		filteredRecordings = recordingData.filter(recording => {
			const recordingDateOnly = new Date(recording.startTime).toISOString().split('T')[0];
			const hasValidDuration = recording.duration >= MIN_DURATION_MINUTES;
			const matchesDate = recordingDateOnly === demoDateOnly;

			return matchesDate && hasValidDuration;
		});
	} else {
		// Even without a demo date, filter out short recordings
		filteredRecordings = recordingData.filter(recording => {
			const hasValidDuration = recording.duration >= MIN_DURATION_MINUTES;
			return hasValidDuration;
		});
	}

	// Enhance recordings with actual video/transcript selection
	const enhancedRecordings = filteredRecordings.map(recording => {
		const actualVideoRecording = selectBestVideoRecording(recording.allRecordings, recording.videoRecording);
		const actualTranscript = selectBestTranscript(recording.allRecordings, recording.transcript);

		return {
			...recording,
			actualVideoRecording,
			actualTranscript,
		};
	});

	// Find the best match based on closest time to demo time
	let bestMatch = null;
	let reorderedRecordings = [...enhancedRecordings];

	if (enhancedRecordings.length > 0) {
		if (demoDate) {
			// Find the recording with start time closest to the demo time
			const demoTime = new Date(demoDate).getTime();
			let closestIndex = 0;
			let smallestDiff = Math.abs(new Date(enhancedRecordings[0].startTime).getTime() - demoTime);

			for (let i = 1; i < enhancedRecordings.length; i++) {
				const recordingTime = new Date(enhancedRecordings[i].startTime).getTime();
				const diff = Math.abs(recordingTime - demoTime);
				if (diff < smallestDiff) {
					closestIndex = i;
					smallestDiff = diff;
				}
			}
			bestMatch = 0; // Will always be at index 0 after reordering

			// Reorder: best match first, then rest sorted by date ascending
			const bestMatchRecording = enhancedRecordings[closestIndex];
			const otherRecordings = enhancedRecordings
				.filter((_, index) => index !== closestIndex)
				.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

			reorderedRecordings = [bestMatchRecording, ...otherRecordings];
		} else {
			// No demo date provided - just sort all by date ascending
			reorderedRecordings = enhancedRecordings.sort(
				(a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
			);
			bestMatch = 0; // First recording after sorting
		}
	}

	return {
		recordings: reorderedRecordings,
		bestMatchIndex: bestMatch,
	};
};

export function useDemoRecordingsQuery({ demoId, demoDate }: { demoId: string; demoDate?: string }) {
	const userSession = useUserSession();
	const apiClient = useRef(new ApiClient(userSession)).current;

	return useQuery({
		enabled: !!demoId,
		queryFn: async (): Promise<IProcessedDemoRecordings> => {
			try {
				const response = await apiClient.getDemoRecordings<IDemoRecordingsResponse>(demoId);

				if (response.success && response.value) {
					const recordingData = response.value.values || [];
					return processRecordings(recordingData, demoDate);
				} else {
					return { recordings: [], bestMatchIndex: null };
				}
			} catch (err: unknown) {
				// Handle specific API error messages based on the AIDA API response format
				const isApiError = (apiError: unknown): apiError is { statusCode: number; message?: string } => {
					return typeof apiError === 'object' && apiError !== null && 'statusCode' in apiError;
				};

				if (isApiError(err)) {
					if (err.statusCode === 400) {
						throw new Error(err.message || 'Demo recordings not available for this demo.');
					} else if (err.statusCode === 401) {
						throw new Error('You do not have permission to view demo recordings.');
					} else {
						throw new Error('Failed to load demo recordings. Please try again.');
					}
				} else {
					throw new Error('Failed to load demo recordings. Please try again.');
				}
			}
		},
		queryKey: [BASE_QUERY_KEY, demoId, demoDate],
	});
}

export function invalidateDemoRecordingsQuery(demoId: string) {
	return reactQueryClient.invalidateQueries({
		queryKey: [BASE_QUERY_KEY, demoId],
		exact: false,
	});
}
